import React from 'react';
import { Target, BarChart2 } from 'lucide-react';

const MarketOpportunity = () => {
  return (
    <div className="space-y-8">
      <div className="bg-white p-8 rounded-xl shadow-md">
        <h3 className="text-2xl font-bold text-primary mb-6">Market Opportunity</h3>
        <div className="flex flex-col md:flex-row">
          <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
            <p className="text-gray-700 mb-4">
              The global healthcare AI market is experiencing explosive growth, projected to reach £250 billion by 2032,
              with a compound annual growth rate of 37.1%.
            </p>
            <p className="text-gray-700 mb-4">
              Within this broader market, approximately 30% is focused on diagnostics and biomarkers,
              representing a £80 billion serviceable available market for Senti's technology.
            </p>
            <p className="text-gray-700">
              With our first-mover advantage in the Large Biosignal Model space, Senti is positioned to
              secure a significant share of this rapidly growing market, similar to how OpenAI captured 75%
              of the LLM market through early innovation.
            </p>
          </div>
          <div className="md:w-1/2 flex justify-center items-center">
            <div className="w-full max-w-md p-6 bg-lightgray rounded-lg">
              <h4 className="font-semibold text-primary mb-4 text-center">Global Healthcare AI Market Growth</h4>
              <div className="h-64 relative">
                {/* This would be replaced with an actual chart component */}
                <div className="absolute bottom-0 left-0 w-1/4 h-12 bg-secondary opacity-40 rounded-t-md"></div>
                <div className="absolute bottom-0 left-1/4 w-1/4 h-24 bg-secondary opacity-60 rounded-t-md"></div>
                <div className="absolute bottom-0 left-2/4 w-1/4 h-40 bg-secondary opacity-80 rounded-t-md"></div>
                <div className="absolute bottom-0 left-3/4 w-1/4 h-64 bg-secondary rounded-t-md"></div>
                <div className="absolute bottom-0 w-full flex justify-between px-4 -mb-6">
                  <span className="text-xs font-semibold">2023</span>
                  <span className="text-xs font-semibold">2026</span>
                  <span className="text-xs font-semibold">2029</span>
                  <span className="text-xs font-semibold">2032</span>
                </div>
                <div className="absolute -left-6 h-full flex flex-col justify-between py-2">
                  <span className="text-xs font-semibold">£250B</span>
                  <span className="text-xs font-semibold">£125B</span>
                  <span className="text-xs font-semibold">£18B</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-8 rounded-xl shadow-md">
        <h3 className="text-2xl font-bold text-primary mb-6">Serviceable Market</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="bg-lightgray p-6 rounded-lg mb-6">
              <div className="flex items-center mb-4">
                <Target className="h-6 w-6 text-primary mr-2" />
                <h4 className="font-semibold text-primary">Total Addressable Market</h4>
              </div>
              <p className="text-gray-700 mb-4">
                Senti has identified 19 different clinical pathways or "device markets" applicable to our LBM technology,
                representing a combined patient population of approximately 950 million patients annually.
              </p>
              <p className="text-gray-700">
                At an average charge of £60.50 per patient per year, this translates to a total addressable
                market of £62 billion annually.
              </p>
            </div>
            <div className="bg-lightgray p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <BarChart2 className="h-6 w-6 text-primary mr-2" />
                <h4 className="font-semibold text-primary">Serviceable Obtainable Market</h4>
              </div>
              <p className="text-gray-700 mb-4">
                Through strategic partnerships with 15-30 medical device manufacturers, Senti
                targets 10% of the available market, representing £6 billion in annual revenues
                over the next 8 years.
              </p>
              <p className="text-gray-700">
                This conservative estimate accounts for regulatory timelines and partnership development cycles,
                while positioning Senti for significant long-term growth.
              </p>
            </div>
          </div>
          <div className="bg-accent bg-opacity-20 p-6 rounded-lg">
            <h4 className="font-semibold text-primary mb-4">Validated Market Segments</h4>
            <div className="space-y-6">
              <div>
                <h5 className="font-medium text-primary mb-2">Respiratory Disease</h5>
                <div className="flex justify-between text-sm mb-1">
                  <span>Patients</span>
                  <span className="font-semibold">136M</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Annual price per patient</span>
                  <span className="font-semibold">£504</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Annual LBM charge</span>
                  <span className="font-semibold">£49</span>
                </div>
              </div>
              
              <div>
                <h5 className="font-medium text-primary mb-2">Fetal Monitoring</h5>
                <div className="flex justify-between text-sm mb-1">
                  <span>Patients</span>
                  <span className="font-semibold">400K</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Annual price per patient</span>
                  <span className="font-semibold">£210</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Annual LBM charge</span>
                  <span className="font-semibold">£21</span>
                </div>
              </div>
              
              <div>
                <h5 className="font-medium text-primary mb-2">Inflammatory Arthritis</h5>
                <div className="flex justify-between text-sm mb-1">
                  <span>Patients</span>
                  <span className="font-semibold">13M</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Annual price per patient</span>
                  <span className="font-semibold">£650</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Annual LBM charge</span>
                  <span className="font-semibold">£62</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketOpportunity;